import { Utils } from '@cadstrom/sigma-engine-shared';
import { useMemo } from 'react';

import { useActiveDesignContext } from '#components/cad-renderer/context/active-design-context.js';
import { useCoreModelContext } from '#components/cad-renderer/context/core-model-context.js';

import type { Design, ModelWithNetStats, Module, ProjectModule } from '@cadstrom/sigma-engine-shared';

/**
 * Finds a project module by reference designator, resolving an instance-level name (e.g. `U4A`)
 * through its placement's `fullModuleName`.
 *
 * @param coreModel - The model to search.
 * @param moduleReferenceDesignator - The project-level or instance-level reference designator.
 * @param activeDesign - The design the designator was read from, searched ahead of the rest.
 * @returns The matching project module, or `undefined` when there is no match.
 */
function findProjectModule(
	coreModel: ModelWithNetStats | undefined,
	moduleReferenceDesignator: string,
	activeDesign: Design | undefined,
): ProjectModule | undefined {
	if (!coreModel) {
		return undefined;
	}

	const foundModule = coreModel.modules.find((m) => m.name === moduleReferenceDesignator);
	if (foundModule) {
		return foundModule;
	}

	// An instance name is only unique within its own design, so the design the designator came from
	// decides the match; without that, a name repeated across designs resolves by enumeration order.
	const searchOrder =
		activeDesign ? [activeDesign, ...Object.values(coreModel.designs)] : Object.values(coreModel.designs);

	for (const design of searchOrder) {
		const module = design.modules.find((m) => m.name === moduleReferenceDesignator);
		if (module) {
			const projectName = module.fullModuleName ?? module.name;
			return coreModel.modules.find((m) => m.name === projectName);
		}
	}
}

/**
 * Collects a project module's schematic placements, sorted by instance name.
 *
 * @param coreModel - The model holding the designs.
 * @param projectModule - The project module whose placements to collect.
 * @returns The placed schematic modules, empty when the core model or module is absent.
 */
function findModuleSchematicInstances(
	coreModel: ModelWithNetStats | undefined,
	projectModule: ProjectModule | undefined,
): Module[] {
	if (!coreModel || !projectModule) {
		return [];
	}
	return projectModule.instances
		.flatMap((i) => {
			const design = coreModel.designs[i.designId];
			if (design?.modelType !== 'schematic') {
				return [];
			}
			const designModule = design.modules.find((m) => m.ref === i.ref);
			if (!designModule) {
				return [];
			}
			return [designModule];
		})
		.sort((a, b) => Utils.naturalStringSort(a.name, b.name));
}

/**
 * Resolves a project module from the current core model by reference designator.
 *
 * Accepts either the project-level reference designator or a placed instance's name (e.g. `U4A`
 * for a multi-unit part), resolving the latter through the instance's `fullModuleName`.
 *
 * @param moduleReferenceDesignator - The project-level or instance-level reference designator.
 * @returns The matching project module, or `undefined` when the core model is absent or holds no
 *   match.
 */
export function useProjectModule(moduleReferenceDesignator: string): ProjectModule | undefined {
	const { coreModel } = useCoreModelContext(false) ?? {};
	const activeDesign = useActiveDesignContext(false)?.activeDesign;
	return useMemo(
		() => findProjectModule(coreModel, moduleReferenceDesignator, activeDesign),
		[coreModel, moduleReferenceDesignator, activeDesign],
	);
}

/**
 * The schematic placements of a project module, sorted by instance name.
 *
 * @param projectModule - The project module whose placements to collect.
 * @returns The placed schematic modules, empty when the core model or module is absent.
 */
export function useModuleSchematicInstances(projectModule: ProjectModule | undefined): Module[] {
	const { coreModel } = useCoreModelContext(false) ?? {};
	return useMemo(() => findModuleSchematicInstances(coreModel, projectModule), [coreModel, projectModule]);
}
